import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowDown } from "@phosphor-icons/react";

const EASE = [0.22, 1, 0.36, 1];

const MaskedLine = ({ children, delay = 0 }) => (
    <span className="block overflow-hidden pb-[0.08em] -mb-[0.08em]">
        <motion.span
            className="block will-change-transform"
            initial={{ y: "115%" }}
            animate={{ y: "0%" }}
            transition={{ duration: 1.1, delay, ease: EASE }}
        >
            {children}
        </motion.span>
    </span>
);

const STATS = [
    { k: "25+", v: "Years installing locally" },
    { k: "16 yr", v: "Manufacturer warranty" },
    { k: "100%", v: "Pet & kid-safe materials" },
    { k: "0", v: "Mowing · Watering · Weeding" },
];

export const Hero = () => {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ["start start", "end start"],
    });
    const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
    const imgScale = useTransform(scrollYProgress, [0, 1], [1.02, 1.16]);
    const fade = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

    return (
        <section
            id="top"
            ref={ref}
            data-testid="hero-section"
            className="relative flex min-h-[100svh] flex-col justify-end overflow-hidden"
        >
            {/* Parallax cabin backdrop */}
            <motion.div aria-hidden className="absolute inset-0" style={{ y: imgY, scale: imgScale }}>
                <motion.img
                    src="/images/cabin-hero.webp"
                    alt="Idaho cabin at dusk with a pristine artificial turf putting green, warm light in the windows"
                    className="h-full w-full object-cover"
                    initial={{ scale: 1.12, opacity: 0.6 }}
                    animate={{ scale: 1.02, opacity: 1 }}
                    transition={{ duration: 2.2, ease: EASE }}
                />
            </motion.div>
            {/* Cinematic grade overlays */}
            <div aria-hidden className="absolute inset-0 bg-gradient-to-b from-black/50 via-transparent to-transparent" />
            <div aria-hidden className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/20 to-transparent" />
            <div
                aria-hidden
                className="pointer-events-none absolute -left-40 bottom-0 h-[420px] w-[720px] rounded-full bg-[#E3A94E]/15 blur-[120px]"
            />

            <motion.div style={{ opacity: fade }} className="relative mx-auto w-full max-w-7xl px-6 pb-10 pt-56 md:px-10 md:pb-14">
                <motion.p
                    initial={{ opacity: 0, y: 14 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.9, ease: EASE }}
                    className="mb-6 flex items-center gap-3 text-xs font-bold uppercase tracking-[0.3em] text-[#F0C06D]"
                >
                    <span aria-hidden className="h-px w-10 bg-[#F0C06D]/70" />
                    Treasure Valley, Idaho — Turf &amp; Putting Greens
                </motion.p>

                <h1
                    data-testid="hero-headline"
                    className="font-display max-w-6xl text-balance text-[12.5vw] font-semibold leading-[0.98] tracking-tight text-[#F5F2E9] sm:text-[10vw] lg:text-[7.6vw]"
                >
                    <MaskedLine delay={0.15}>Artificial turf &amp;</MaskedLine>
                    <MaskedLine delay={0.28}>
                        <span className="italic text-[#F0C06D]">putting greens</span>,
                    </MaskedLine>
                    <MaskedLine delay={0.41}>perfected for Idaho.</MaskedLine>
                </h1>

                <div className="mt-8 flex flex-col gap-8 md:flex-row md:items-end md:justify-between">
                    <motion.p
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 1.05, ease: EASE }}
                        className="max-w-xl text-base leading-relaxed text-white/80 md:text-lg"
                        data-testid="hero-subtext"
                    >
                        Premium PFAS-free turf and tournament-grade greens,
                        hand-laid by a local crew. Looks real in July, perfect
                        in January — and never needs a drop of water.
                    </motion.p>

                    <motion.div
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 1.2, ease: EASE }}
                        className="flex flex-wrap items-center gap-4"
                    >
                        <a
                            href="#quote"
                            data-testid="hero-cta-primary"
                            className="group inline-flex items-center gap-2 rounded-full bg-brand-accent px-7 py-4 text-base font-bold text-white transition-all duration-300 hover:bg-brand-accentHover hover:shadow-[0_14px_44px_-8px_rgba(227,169,78,0.55)]"
                        >
                            Get your free quote
                            <ArrowRight size={18} weight="bold" className="transition-transform duration-300 group-hover:translate-x-1" />
                        </a>
                        <a
                            href="#services"
                            data-testid="hero-cta-secondary"
                            className="inline-flex items-center gap-2 rounded-full border border-white/30 px-7 py-4 text-base font-semibold text-white backdrop-blur-sm transition-colors duration-300 hover:border-[#F0C06D] hover:text-[#F0C06D]"
                        >
                            See our work
                        </a>
                    </motion.div>
                </div>

                {/* Stats strip */}
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 1, delay: 1.5 }}
                    className="mt-14 grid grid-cols-2 gap-y-6 border-t border-white/20 pt-6 md:grid-cols-4"
                >
                    {STATS.map((s, i) => (
                        <div key={s.k} data-testid={`hero-stat-${i}`} className="pr-6">
                            <div className="font-display text-3xl font-light italic text-white md:text-4xl">
                                {s.k}
                            </div>
                            <div className="mt-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-white/60">
                                {s.v}
                            </div>
                        </div>
                    ))}
                </motion.div>
            </motion.div>

            {/* Scroll cue */}
            <motion.div
                aria-hidden
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 2, duration: 1 }}
                className="pointer-events-none absolute right-8 top-1/2 hidden -translate-y-1/2 flex-col items-center gap-3 lg:flex"
            >
                <span className="text-[10px] font-bold uppercase tracking-[0.3em] text-white/60 [writing-mode:vertical-rl]">
                    Scroll
                </span>
                <motion.span
                    animate={{ y: [0, 8, 0] }}
                    transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
                >
                    <ArrowDown size={14} className="text-[#F0C06D]" />
                </motion.span>
            </motion.div>
        </section>
    );
};
